import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { API } from "../../lib/api";

/* "Latest from the Community" strip on the Home page — three most recent
   comments/posts with the poster's name, snippet and photo. Silent no-op
   when the feed can't be loaded. */

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.max(1, Math.round((Date.now() - t) / 60000));
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30);
  return `${mo}mo ago`;
}

function absoluteImage(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api")) return `${API}${url.slice(4)}`;
  return url;
}

export default function HomeCommunityFeed({ limit = 3 }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/community/feed").then((r) => {
      const seen = new Set();
      const filtered = [];
      for (const p of r.data.items || []) {
        if (seen.has(p.user_id)) continue;
        seen.add(p.user_id);
        filtered.push(p);
        if (filtered.length >= limit) break;
      }
      setItems(filtered);
    }).catch(() => setItems([]));
  }, [limit]);

  if (items === null || items.length === 0) return null;

  return (
    <section data-testid="home-community-feed">
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="font-serif font-800 text-2xl text-primary leading-tight">Latest from the Community</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Real notes from travellers on the road right now.</p>
        </div>
        <Link to="/community" data-testid="home-community-feed-more"
          className="inline-flex items-center gap-1 text-xs font-700 text-accent hover:underline">
          See all <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((p) => {
          const img = absoluteImage(p.image_url);
          const snippet = (p.title || p.text || "").slice(0, 140);
          return (
            <Link key={p.id} to={`/community/post/${p.id}`} data-testid={`home-community-card-${p.id}`}
              className="group flex flex-col bg-card border border-border rounded-2xl overflow-hidden hover:border-accent hover:shadow-warm-md transition-[border-color,box-shadow] tap">
              {img && (
                <div className="aspect-[16/9] relative bg-secondary/40">
                  <img src={img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4 flex-1 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {p.user_picture ? (
                    <img src={p.user_picture} alt="" className="w-6 h-6 rounded-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-[10px] font-800 inline-flex items-center justify-center">
                      {(p.user_name || "T").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="text-xs">
                    <div className="font-700 text-primary leading-tight truncate">{p.user_name || "Traveller"}</div>
                    <div className="text-[10px] text-muted-foreground">{relTime(p.created_at)}</div>
                  </div>
                </div>
                {p.title && <h3 className="font-serif font-700 text-base text-primary leading-snug line-clamp-2">{p.title}</h3>}
                {snippet && !p.title && <p className="text-sm text-foreground/80 leading-snug line-clamp-3">{snippet}</p>}
                {snippet && p.title && <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{snippet}</p>}
                <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5" strokeWidth={1.75} /> {p.like_count || 0}</span>
                  <span className="inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" strokeWidth={1.75} /> {p.reaction_total || 0}</span>
                  {p.entity_name && <span className="ml-auto truncate text-[11px]">on {p.entity_name}</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
